import { baseURL } from '../config';

/**
 * Account Management API Service
 * Handles all account-related API calls
 */

/**
 * Create a new account for a user
 * @param {string} token - Authentication token
 * @param {object} accountData - Account data including userId, accountNumber, accountType, currency, etc.
 * @returns {Promise<object>} Response with success status and data
 */
export const createAccount = async (token, accountData) => {
    try {
        console.log('Creating account:', accountData);

        const response = await fetch(`${baseURL}/api/Account`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(accountData)
        });

        console.log('Create account response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to create account' }));
            console.error('Create account error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`
            };
        }

        const data = await response.json();
        console.log('Account created successfully:', data);

        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('Error in createAccount:', error);
        return {
            success: false,
            error: error.message || 'Failed to create account'
        };
    }
};

/**
 * Get all accounts
 * @param {string} token - Authentication token
 * @returns {Promise<object>} Response with success status and account data
 */
export const getAllAccounts = async (token) => {
    try {
        console.log('Fetching all accounts...');

        const response = await fetch(`${baseURL}/api/Account`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        console.log('Get all accounts response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to fetch accounts' }));
            console.error('Get accounts error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`,
                data: []
            };
        }

        const data = await response.json();
        console.log('Accounts fetched successfully:', data);

        // Handle different response formats
        let accounts = [];
        if (Array.isArray(data)) {
            accounts = data;
        } else if (data.data && Array.isArray(data.data)) {
            accounts = data.data;
        } else if (data.accounts && Array.isArray(data.accounts)) {
            accounts = data.accounts;
        }

        return {
            success: true,
            data: accounts
        };
    } catch (error) {
        console.error('Error in getAllAccounts:', error);
        return {
            success: false,
            error: error.message || 'Failed to fetch accounts',
            data: []
        };
    }
};

/**
 * Get accounts for the current logged-in user
 * Uses /api/Account/user/{userId} endpoint (User-specific, secure)
 * @param {string} token - Authentication token
 * @param {string} userId - User ID from JWT token
 * @returns {Promise<object>} Response with success status and account data
 */
export const getAccountsByUserId = async (token, userId) => {
    try {
        console.log('Fetching accounts for user ID:', userId);

        const response = await fetch(`${baseURL}/api/Account/user/${userId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        console.log('Get accounts by user ID response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to fetch user accounts' }));
            console.error('Get accounts by user ID error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`,
                data: []
            };
        }

        const data = await response.json();
        console.log('User accounts fetched successfully:', data);

        // Handle different response formats
        let accounts = [];
        if (Array.isArray(data)) {
            accounts = data;
        } else if (data.data && Array.isArray(data.data)) {
            accounts = data.data;
        } else if (data.accounts && Array.isArray(data.accounts)) {
            accounts = data.accounts;
        }

        return {
            success: true,
            data: accounts
        };
    } catch (error) {
        console.error('Error in getAccountsByUserId:', error);
        return {
            success: false,
            error: error.message || 'Failed to fetch user accounts',
            data: []
        };
    }
};

/**
 * Get accounts for a specific user (Admin endpoint - for admin panel)
 * @param {string} token - Authentication token
 * @param {string} userId - User ID
 * @returns {Promise<object>} Response with success status and account data
 */
export const getUserAccounts = async (token, userId) => {
    try {
        console.log('Fetching accounts for user:', userId);

        const response = await fetch(`${baseURL}/api/Admin/users/${userId}/accounts`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        console.log('Get user accounts response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to fetch user accounts' }));
            console.error('Get user accounts error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`,
                data: []
            };
        }

        const data = await response.json();
        console.log('User accounts fetched successfully:', data);

        // Handle different response formats
        let accounts = [];
        if (Array.isArray(data)) {
            accounts = data;
        } else if (data.data && Array.isArray(data.data)) {
            accounts = data.data;
        } else if (data.accounts && Array.isArray(data.accounts)) {
            accounts = data.accounts;
        }

        return {
            success: true,
            data: accounts
        };
    } catch (error) {
        console.error('Error in getUserAccounts:', error);
        return {
            success: false,
            error: error.message || 'Failed to fetch user accounts',
            data: []
        };
    }
};

/**
 * Update an existing account
 * @param {string} token - Authentication token
 * @param {number|string} accountNumber - Account Number (not ID)
 * @param {object} accountData - Updated account data
 * @returns {Promise<object>} Response with success status and data
 */
export const updateAccount = async (token, accountNumber, accountData) => {
    try {
        console.log('Updating account:', accountNumber, accountData);

        const response = await fetch(`${baseURL}/api/Account/${accountNumber}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(accountData)
        });

        console.log('Update account response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to update account' }));
            console.error('Update account error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`
            };
        }

        const data = await response.json();
        console.log('Account updated successfully:', data);

        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('Error in updateAccount:', error);
        return {
            success: false,
            error: error.message || 'Failed to update account'
        };
    }
};

/**
 * Delete an account
 * @param {string} token - Authentication token
 * @param {number|string} accountNumber - Account Number (not ID)
 * @returns {Promise<object>} Response with success status
 */
export const deleteAccount = async (token, accountNumber) => {
    try {
        console.log('Deleting account:', accountNumber);

        const response = await fetch(`${baseURL}/api/Account/${accountNumber}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        console.log('Delete account response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to delete account' }));
            console.error('Delete account error:', errorData);
            return {
                success: false,
                error: errorData.message || `HTTP error! status: ${response.status}`
            };
        }

        console.log('Account deleted successfully');

        return {
            success: true
        };
    } catch (error) {
        console.error('Error in deleteAccount:', error);
        return {
            success: false,
            error: error.message || 'Failed to delete account'
        };
    }
};

// Export all functions as a single object for convenience
export const accountApi = {
    createAccount,
    getAllAccounts,
    getAccountsByUserId,
    getUserAccounts,
    updateAccount,
    deleteAccount
};

export default accountApi;
